import { CalendarDate } from "@internationalized/date";
import { useEffect } from "react";

import { useGetRequest } from "@/hooks/useGetRequest.tsx";

import { mapAttachmentsToAssets } from "../utils/utils";
import { useEditRequestForm } from "./useEditRequestForm";
import { useGetAttachments } from "./useGetAttachments";

export function useInitializeRequestForm() {
  const formMethods = useEditRequestForm();

  const { data: request } = useGetRequest();
  const { data: attachments } = useGetAttachments();

  useEffect(() => {
    if (!request) return;
    if (!attachments) return;

    const { assets, example } = mapAttachmentsToAssets(attachments);

    formMethods.reset({
      assets: assets,
      assignee: request.assignee,
      brandId: request.brandId,
      briefText: request.briefText ?? undefined,
      collaboratorIds: request.collaboratorsIds ?? undefined,
      complexityId: request.complexityId,
      creativeDirection: request.creativeDirection ?? undefined,
      creatorId: request.creator?.id,
      description: request.description ?? undefined,
      designerId: request.designerId ?? undefined,
      designTypeId: request.designTypeId,
      dueDate: toCalendarDate(request.customerDueDate),
      fileTypes: request.fileTypes ?? undefined,
      inspirations: example,
      internalDueDate: toCalendarDate(request.internalDueDate),
      internalNotes: request.internalNotes ?? "",
      lengthOrPlatform: request.lengthOrPlatform ?? undefined,
      linkedRequestIds: request.linkedRequestIds ?? [],
      magicalBriefCreator: {
        additionalGuidelines:
          request?.magicalBriefCreator?.additionalGuidelines,
        brandGuidelines: request?.magicalBriefCreator?.brandGuidelines,
        creativeGoals: request?.magicalBriefCreator?.creativeGoals,
        keyMessageAndCta: request?.magicalBriefCreator?.keyMessageAndCta,
        scope: request?.magicalBriefCreator?.scope,
        script: request?.magicalBriefCreator?.script,
      },
      musicGenre: request.musicGenre ?? undefined,
      name: String(request.name) || "",
      preferredDesignerIds: request.preferredDesignerIds ?? undefined,
      preferredFileType: request.preferredFileType ?? undefined,
      restrictAiAssets: request.restrictAiAssets ?? false,
      sizesNeeded: request.sizesNeeded ?? undefined,
      statusId: request.statusId ?? null,
      styleOfVideo: request.styleOfVideo ?? undefined,
      textForDesign: request.textForDesign ?? undefined,
    });
  }, [request, attachments]);

  return {
    attachments,
    formMethods,
    request,
    requestId: request?.id,
  };
}

function toCalendarDate(jsDateObject: Date | null | string | undefined) {
  if (!jsDateObject) return null;

  let dateObj: Date;

  if (typeof jsDateObject === "string") {
    dateObj = new Date(jsDateObject);
    if (Number.isNaN(dateObj.getTime())) {
      console.warn("Invalid date string:", jsDateObject);

      return null;
    }
  } else {
    dateObj = jsDateObject;
  }

  const formatter = new Intl.DateTimeFormat("en-CA", {
    day: "2-digit",
    month: "2-digit",
    timeZone: "America/New_York",
    year: "numeric",
  });

  const dateString = formatter.format(dateObj);
  const [yearStr = "1970", monthStr = "1", dayStr = "1"] =
    dateString.split("-");

  const day = Number.parseInt(dayStr, 10);
  const month = Number.parseInt(monthStr, 10);
  const year = Number.parseInt(yearStr, 10);

  return new CalendarDate(year, month, day);
}
